import { CallHistoryFilters } from '../models/entity-leads.types';

export function parseOutcomeNamesFromResponse(res: any): string[] {
  const names = new Set<string>();

  const addName = (value: any) => {
    if (typeof value === 'string' && value.trim()) {
      names.add(value.trim());
      return;
    }
    if (value && typeof value.outcome_name === 'string' && value.outcome_name.trim()) {
      names.add(value.outcome_name.trim());
    }
    if (value && typeof value.name === 'string' && value.name.trim()) {
      names.add(value.name.trim());
    }
  };

  if (!res) {
    return [];
  }

  if (Array.isArray(res)) {
    res.forEach(addName);
    return Array.from(names).sort();
  }

  const collections = [
    res.outcomes,
    res.items,
    res.data,
    res.expected_outcomes,
    res.result,
  ];

  collections.forEach((collection) => {
    if (Array.isArray(collection)) {
      collection.forEach(addName);
    }
  });

  if (res.expected_outcomes && Array.isArray(res.expected_outcomes)) {
    res.expected_outcomes.forEach(addName);
  }

  const projects = (res && res.items) || (Array.isArray(res) ? res : []);
  if (Array.isArray(projects)) {
    projects.forEach((project: any) => {
      if (project && Array.isArray(project.expected_outcomes)) {
        project.expected_outcomes.forEach(addName);
      }
    });
  }

  return Array.from(names).sort();
}

export function buildCallHistoryFilters(options: {
  searchQuery?: string;
  selectedOutcomes?: string[];
  selectedOutcome?: string | null;
  allOutcomesLabel?: string;
  isOutcomesMode?: boolean;
}): CallHistoryFilters | undefined {
  const filters: CallHistoryFilters = {};
  const allLabel = options.allOutcomesLabel || 'ALL';

  if (options.isOutcomesMode) {
    if (options.selectedOutcomes && options.selectedOutcomes.length > 0) {
      filters.status = options.selectedOutcomes.join(',');
    }
  } else if (options.selectedOutcome && options.selectedOutcome !== allLabel) {
    filters.status = options.selectedOutcome;
  }

  const query = (options.searchQuery || '').trim();
  if (query) {
    if (isPhoneQuery(query)) {
      filters.mobile = query;
    } else {
      filters.name = query;
    }
  }

  return Object.keys(filters).length > 0 ? filters : undefined;
}

export function isPhoneQuery(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7;
}

export function normalizePagination(pagination: any): any | null {
  if (!pagination) {
    return null;
  }
  const toNum = (v: any) => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : v;
  };
  return {
    ...pagination,
    currentPageNum: toNum(pagination.currentPageNum || pagination.current_page),
    totalPages: toNum(pagination.totalPages || pagination.total_pages),
    currentPageSize: toNum(pagination.currentPageSize || pagination.page_size),
    totalElements: toNum(pagination.totalElements || pagination.total_records),
    has_next_page: pagination.has_next_page,
    has_previous_page: pagination.has_previous_page,
  };
}

export function buildPageNumbers(pagination: any): Array<number | string> {
  if (!pagination) {
    return [];
  }
  const totalPages = Number(pagination.totalPages) || 0;
  const currentPage = Number(pagination.currentPageNum) || 1;
  if (!totalPages) {
    return [];
  }

  const pages: Array<number | string> = [];
  const addPage = (page: number | string) => {
    if (pages[pages.length - 1] !== page) {
      pages.push(page);
    }
  };

  if (totalPages <= 6) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  addPage(1);
  if (currentPage > 3) {
    addPage('...');
  }
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);
  for (let i = startPage; i <= endPage; i++) {
    addPage(i);
  }
  if (currentPage < totalPages - 2) {
    addPage('...');
  }
  addPage(totalPages);
  return pages;
}

export function getPaginationRange(pagination: any): string {
  if (pagination && pagination.currentPageNum && pagination.currentPageSize && pagination.totalElements) {
    const start = (pagination.currentPageNum - 1) * pagination.currentPageSize + 1;
    const end = Math.min(pagination.currentPageNum * pagination.currentPageSize, pagination.totalElements);
    return `${start} - ${end} of ${pagination.totalElements} records`;
  }
  return '0 - 0 of 0 records';
}

export function canPrevPage(pagination: any): boolean {
  if (!pagination) {
    return false;
  }
  if (pagination.has_previous_page !== undefined) {
    return pagination.has_previous_page === true;
  }
  return (Number(pagination.currentPageNum) || 1) > 1;
}

export function canNextPage(pagination: any): boolean {
  if (!pagination) {
    return false;
  }
  if (pagination.has_next_page !== undefined) {
    return pagination.has_next_page === true;
  }
  const currentPage = Number(pagination.currentPageNum) || 1;
  const totalPages = Number(pagination.totalPages) || 0;
  return totalPages > 0 && currentPage < totalPages;
}

export function enrichCallRows(calls: any[], formatStartTime: boolean): any[] {
  return calls.map((ele) => {
    if (!ele || !ele.start_time) {
      return ele;
    }
    const timestamp = typeof ele.start_time === 'string' ? parseFloat(ele.start_time) : ele.start_time;
    if (typeof timestamp === 'number' && !isNaN(timestamp) && timestamp > 0) {
      ele._raw_start_time = timestamp;
      if (formatStartTime) {
        ele.start_time = formatStartTimeLabel(timestamp);
      }
    }
    return ele;
  });
}

export function formatStartTimeLabel(value: number): string {
  const date = new Date(value * 1000);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()] || '';
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const pad = (num: number) => (num < 10 ? `0${num}` : `${num}`);
  return `${month} ${day}, ${year}, ${formattedHours}:${pad(minutes)} ${period}`;
}
